import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Brain, AlertCircle, History, ArrowLeft, ArrowRight, Sparkles, Calendar, MessageCircle, Volume2 } from '../components/icons';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { clearAssessmentDraft } from '../hooks/useQuestionnaire';
import { ResultsGraph } from '../components/ResultsGraph';
import { HarmonicGraph } from '../components/HarmonicGraph';
import { AIAnalysisPanel } from '../components/ai/AIAnalysisPanel';
import { AICoachChat } from '../components/ai-coach/AICoachChat';
import { AICoachVoice } from '../components/ai-coach/AICoachVoice';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { MyPathways } from '../components/pathway/MyPathways';
import { fetchMyAICoachAccess, fetchCoachConfigForRespondent } from '../lib/ai-coach';
import { fetchMyPathways } from '../lib/pathways';
import type { AssessmentResult } from '../types/results';
import type { AssignedPathway } from '../types/pathway';

const TOTAL_QUESTIONS = 180;

interface UserProfile {
  full_name: string | null;
  email: string | null;
}

export function RespondentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [results, setResults] = useState<AssessmentResult[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [canRetake, setCanRetake] = useState(false);
  const [nextRetakeDate, setNextRetakeDate] = useState<Date | null>(null);
  const [hasAiAccess, setHasAiAccess] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [chatEnabled, setChatEnabled] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [elevenlabsAgentId, setElevenlabsAgentId] = useState('');
  const [showChat, setShowChat] = useState(false);
  const [showVoice, setShowVoice] = useState(false);
  const [myPathways, setMyPathways] = useState<AssignedPathway[]>([]);
  const [savedDraftIndex, setSavedDraftIndex] = useState<number | null>(null);

  const loadResults = useCallback(async (userId: string) => {
    try {
      const { data: resultsData, error: resultsError } = await supabase
        .from('results')
        .select('*')
        .eq('user_id', userId)
        .order('completed_at', { ascending: false });

      if (resultsError) throw resultsError;

      if (resultsData && resultsData.length > 0) {
        const allResults: AssessmentResult[] = resultsData.map(doc => ({
          id: doc.id,
          userId: doc.user_id,
          coachId: doc.coach_id || undefined,
          partnerId: doc.partner_id || undefined,
          scores: (doc.scores || []) as any,
          harmonicScores: (doc.harmonic_scores || []) as any,
          completedAt: new Date(doc.completed_at || doc.created_at || Date.now()),
          aiAnalysis: doc.coach_ai_analysis || doc.ai_analysis || undefined,
          coachAiAnalysis: doc.coach_ai_analysis || undefined,
          version: doc.version || 1,
          originalResultId: doc.original_result_id || undefined,
        }));

        setResults(allResults);

        const { data: respondentData } = await supabase
          .from('respondents')
          .select('next_retake_date, is_active, coach_id, ai_analysis_visible')
          .eq('user_id', userId)
          .maybeSingle();

        if (respondentData) {
          const nextRetake = respondentData.next_retake_date
            ? new Date(respondentData.next_retake_date)
            : null;
          const retakeEnabled = respondentData.is_active !== false;

          setCanRetake(retakeEnabled && (!nextRetake || nextRetake <= new Date()));
          setNextRetakeDate(nextRetake);

          if (respondentData.coach_id) {
            const { data: coachData } = await supabase
              .from('coaches')
              .select('tier')
              .eq('id', respondentData.coach_id)
              .maybeSingle();

            if (coachData) {
              const coachTierAllows = coachData.tier === 'advanced' || coachData.tier === 'partner';
              const visibleToRespondent = respondentData.ai_analysis_visible === true;
              setHasAiAccess(coachTierAllows && visibleToRespondent);
            }
          }

          const { data: respRecord } = await supabase
            .from('respondents')
            .select('id, coach_id')
            .eq('user_id', userId)
            .maybeSingle();

          if (respRecord?.coach_id) {
            const [access, coachConfig, pathways] = await Promise.all([
              fetchMyAICoachAccess(respRecord.id),
              fetchCoachConfigForRespondent(respRecord.coach_id),
              fetchMyPathways(respRecord.id),
            ]);

            if (coachConfig?.isEnabled) {
              setChatEnabled(access ? access.chatEnabled : true);
              setVoiceEnabled(access ? access.voiceEnabled : true);
              setElevenlabsAgentId(coachConfig.elevenlabsAgentId || '');
            }

            setMyPathways(pathways);
          }
        }
      }
    } catch (err: any) {
      console.error('Error loading results:', err);
      setError(err.message || 'Failed to load results');
    }
  }, []);

  useEffect(() => {
    if (!user) return;

    const loadData = async () => {
      try {
        const { data: profile } = await supabase
          .from('users')
          .select('full_name, email')
          .eq('id', user.id)
          .maybeSingle();

        if (profile) {
          setUserProfile(profile);
        } else {
          setUserProfile({ full_name: null, email: user.email ?? null });
        }

        await loadResults(user.id);

        const { data: draft } = await supabase
          .from('assessment_drafts')
          .select('current_index')
          .eq('user_id', user.id)
          .eq('assessment_type', 'standard')
          .maybeSingle();

        if (draft && draft.current_index > 0) {
          setSavedDraftIndex(draft.current_index);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [user, loadResults]);

  const handlePreviousResult = () => {
    if (currentPage < results.length) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handleNextResult = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleAnalysisUpdate = (analysis: string) => {
    setResults(prev => prev.map((r, i) =>
      i === currentPage - 1 ? { ...r, aiAnalysis: analysis } : r
    ));
  };

  const handleRetake = async () => {
    if (user) {
      await clearAssessmentDraft(user.id);
    }
    navigate('/assessment');
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading your results..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen freud-bg py-8 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="freud-card-elevated p-8 text-center">
            <div className="flex items-center gap-2 bg-warm-100 text-warm-700 p-4 rounded-xl mb-6">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p>{error}</p>
            </div>
            <Link
              to="/assessment"
              className="freud-btn-primary inline-flex items-center gap-2"
            >
              Take Assessment
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="min-h-screen freud-bg py-8 px-6">
        <div className="max-w-lg mx-auto">
          {savedDraftIndex !== null && savedDraftIndex > 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="freud-card-elevated p-10 text-center"
            >
              <div className="w-16 h-16 bg-sage-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-sage-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <h2 className="freud-heading-xl text-chocolate-900 mb-3">Assessment In Progress</h2>
              <p className="freud-text-base text-chocolate-500 mb-2">
                You have a saved assessment — question {savedDraftIndex + 1} of {TOTAL_QUESTIONS} completed.
              </p>
              <p className="text-sm text-chocolate-400 mb-8">Pick up right where you left off, or start over.</p>
              <div className="flex flex-col gap-3">
                <Link
                  to="/assessment"
                  className="freud-btn-primary inline-flex items-center justify-center gap-2"
                >
                  Continue Assessment
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <button
                  onClick={async () => {
                    if (user) await clearAssessmentDraft(user.id);
                    setSavedDraftIndex(null);
                    navigate('/assessment');
                  }}
                  className="freud-btn-secondary"
                >
                  Start from the beginning
                </button>
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="freud-card-elevated p-10 text-center"
            >
              <div className="w-16 h-16 bg-sage-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Brain className="w-8 h-8 text-sage-600" />
              </div>
              <h2 className="freud-heading-xl text-chocolate-900 mb-4">No Assessment Results Found</h2>
              <p className="freud-text-base text-chocolate-500 mb-8 max-w-md mx-auto">You haven't completed an assessment yet. Take your first assessment to get personalized insights.</p>
              <Link
                to="/assessment"
                className="freud-btn-primary inline-flex items-center gap-2"
              >
                Take Assessment
                <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          )}
        </div>
      </div>
    );
  }

  const selectedResult = results[currentPage - 1];
  const totalVersions = results.length;

  return (
    <div className="min-h-screen freud-bg py-8 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="freud-heading-2xl text-chocolate-900">Your Assessment Results</h1>
            <div className="flex items-center gap-2 text-chocolate-500 mt-1">
              <Calendar className="w-4 h-4" />
              <span>Version {totalVersions - currentPage + 1}</span>
              <span>- Completed on {selectedResult.completedAt.toLocaleDateString()}</span>
            </div>
          </div>

          {results.length > 1 && (
            <div className="flex items-center gap-4">
              <button
                onClick={handleNextResult}
                disabled={currentPage <= 1}
                className="p-2.5 rounded-xl bg-cream-200 text-chocolate-700 hover:bg-cream-300 transition-all disabled:opacity-50"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="text-chocolate-600 font-medium">
                {currentPage} of {totalVersions}
              </div>
              <button
                onClick={handlePreviousResult}
                disabled={currentPage >= totalVersions}
                className="p-2.5 rounded-xl bg-cream-200 text-chocolate-700 hover:bg-cream-300 transition-all disabled:opacity-50"
              >
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="freud-card-elevated p-8"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-sage-100 rounded-xl flex items-center justify-center">
                <Brain className="w-5 h-5 text-sage-600" />
              </div>
              <h2 className="freud-heading-xl text-chocolate-900">
                Characteristic Profile
              </h2>
            </div>
            <ResultsGraph scores={selectedResult.scores} />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="freud-card-elevated p-8"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-warm-100 rounded-xl flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-warm-600" />
              </div>
              <h2 className="freud-heading-xl text-chocolate-900">
                Harmonic Scale Profile
              </h2>
            </div>
            <HarmonicGraph scores={selectedResult.harmonicScores} />
          </motion.div>
        </div>

        {hasAiAccess && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mb-8"
          >
            <AIAnalysisPanel
              analysis={selectedResult.aiAnalysis}
              scores={selectedResult.scores}
              harmonicScores={selectedResult.harmonicScores}
              resultId={selectedResult.id}
              onAnalysisUpdate={handleAnalysisUpdate}
            />
          </motion.div>
        )}

        {!hasAiAccess && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mb-8"
          >
            <div className="text-chocolate-500 text-center bg-cream-100 px-6 py-4 rounded-xl">
              AI Analysis is not available. Please contact your coach for more information.
            </div>
          </motion.div>
        )}

        <MyPathways pathways={myPathways} />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="flex flex-col items-center gap-4"
        >
          {canRetake && (
            <button
              onClick={handleRetake}
              className="freud-btn-primary inline-flex items-center gap-2"
            >
              Retake Assessment
              <ArrowRight className="w-5 h-5" />
            </button>
          )}

          {!canRetake && nextRetakeDate && (
            <div className="flex items-center gap-2 px-4 py-3 bg-sand-100 rounded-xl text-sand-800">
              <Calendar className="w-5 h-5" />
              Next retake available on: {nextRetakeDate.toLocaleDateString()}
            </div>
          )}

          {results.length > 1 && (
            <div className="flex items-center gap-2 text-chocolate-500 mt-4">
              <History className="w-4 h-4" />
              <span>Total Assessments: {results.length}</span>
            </div>
          )}
        </motion.div>
      </div>

      {(chatEnabled || voiceEnabled) && (
        <div className="fixed bottom-6 right-6 flex flex-col gap-3 z-40">
          {voiceEnabled && elevenlabsAgentId && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.2 }}
              onClick={() => { setShowVoice(true); setShowChat(false); }}
              className="w-14 h-14 bg-warm-500 text-white rounded-full shadow-lg hover:bg-warm-600 transition-colors flex items-center justify-center"
              title="Voice AI Coach"
            >
              <Volume2 className="w-6 h-6" />
            </motion.button>
          )}
          {chatEnabled && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.0 }}
              onClick={() => { setShowChat(true); setShowVoice(false); }}
              className="w-14 h-14 bg-sage-600 text-white rounded-full shadow-lg hover:bg-sage-700 transition-colors flex items-center justify-center"
              title="Chat with AI Coach"
            >
              <MessageCircle className="w-6 h-6" />
            </motion.button>
          )}
        </div>
      )}

      {chatEnabled && (
        <AICoachChat isOpen={showChat} onClose={() => setShowChat(false)} />
      )}

      {voiceEnabled && elevenlabsAgentId && (
        <AICoachVoice
          isOpen={showVoice}
          onClose={() => setShowVoice(false)}
          agentId={elevenlabsAgentId}
          respondentName={userProfile?.full_name || ''}
          scores={selectedResult.scores}
          harmonicScores={selectedResult.harmonicScores}
        />
      )}
    </div>
  );
}
